import React, { useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useChatData, useChatClient } from '../context/ChatContext';
import { isValidEthAddress } from '../utils/chatHelpers';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface NewConversationModalProps {
  show: boolean;
  onHide: () => void;
}

const NewConversationModal: React.FC<NewConversationModalProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { createDm, selectConversation } = useChatData();
  const [address, setAddress] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = useCallback(async () => {
    setError('');

    if (!address.trim()) {
      setError(t('chat.enterAddress', 'Please enter a wallet address'));
      return;
    }

    if (!isValidEthAddress(address.trim())) {
      setError(t('chat.invalidAddress', 'Invalid Ethereum address'));
      return;
    }

    setIsCreating(true);
    try {
      const conversationId = await createDm(address.trim());
      if (conversationId) {
        selectConversation(conversationId);
        setAddress('');
        onHide();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start conversation');
    } finally {
      setIsCreating(false);
    }
  }, [address, createDm, selectConversation, onHide, t]);

  const handleClose = () => {
    setAddress('');
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="chat-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('chat.newConversation', 'New Conversation')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="chat-modal__field">
          <label className="chat-modal__label">
            {t('chat.recipientAddress', 'Recipient Wallet Address')}
          </label>
          <input
            type="text"
            className="chat-search-input"
            placeholder="0x..."
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setError('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          {error && <p className="chat-modal__error">{error}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="chat-btn chat-btn--secondary" onClick={handleClose}>
          {t('chat.cancel', 'Cancel')}
        </button>
        <button
          className="chat-btn chat-btn--primary"
          onClick={handleCreate}
          disabled={isCreating || !address.trim()}
        >
          {isCreating ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> {t('chat.creating', 'Creating...')}
            </>
          ) : (
            t('chat.startChat', 'Start Chat')
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewConversationModal;
